import React, { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';

import ContactPcsIcon from 'dibs-icons/exports/CircleContactInfo';
import PersonBubble from 'dibs-icons/exports/PersonBubble';
import { PdpSharedBlurb } from './PdpSharedBlurb/PdpSharedBlurb';
import {
    pcsTitle,
    pcsText,
    pcsTitleSoldPdp,
    pcsTextSoldPdp,
    pcsTextSoldPdpBanner,
    messageCta,
} from '../messages/pdpBlurbMessages';

// styles
import styles from './PdpSharedBlurbs.scss';

const WorkspaceContactVipRepModal = React.lazy(() =>
    import(
        /* webpackChunkName: "PdpContactVipRepModalChunk" */ 'dibs-workspace-intro-modal/exports/ContactVipRepModal'
    )
);

export const PdpSharedBlurbPcsComponent = ({
    user,
    item,
    vipUserRelationship,
    relay,
    isSoldPdp,
    isTopBanner,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const vipSalesRep = vipUserRelationship?.userDataB?.[0];
    const isVip = user?.isVip;
    const userId = user?.serviceId;
    const vipSalesRepFirstName = vipSalesRep?.firstName;
    const [vipPdpBlurbTitle, setVipPdpBlurbTitle] = useState(pcsTitle);
    const [textDescription, setTextDescription] = useState(pcsText(vipSalesRepFirstName));

    useEffect(() => {
        if (isTopBanner && isSoldPdp) {
            const topBannerDescription = pcsTextSoldPdpBanner(vipSalesRepFirstName);
            setTextDescription(topBannerDescription);
        } else if (isSoldPdp) {
            setVipPdpBlurbTitle(pcsTitleSoldPdp);
            const description = pcsTextSoldPdp(vipSalesRepFirstName);
            setTextDescription(description);
        }
    }, [isSoldPdp, isTopBanner, vipPdpBlurbTitle, vipSalesRepFirstName]);

    if (!user || user.isVerifiedTrade || !isVip) {
        return null;
    }

    const repInfo = (
        <Fragment>
            {vipSalesRep?.phoneNumber}
            <div className={styles.pcsDivider} />
            <div className={styles.messageCta} onClick={() => setIsOpen(true)}>
                {messageCta(vipSalesRepFirstName)}
            </div>
            <React.Suspense fallback={null}>
                <WorkspaceContactVipRepModal
                    isVip={isVip}
                    environment={relay.environment}
                    isOpen={isOpen}
                    onClose={() => setIsOpen(false)}
                    itemId={item?.serviceId}
                    userId={userId}
                />
            </React.Suspense>
        </Fragment>
    );

    if (isTopBanner) {
        return (
            <div className={styles.bannerContainer}>
                <div className={styles.bannerDescription}>{textDescription}</div>
                <div className={styles.bannerInfo}>{repInfo}</div>
            </div>
        );
    }

    return (
        <div className={!isTopBanner && isSoldPdp && styles.pdpSharedBlurbPcsContainer}>
            <PdpSharedBlurb
                IconComponent={isSoldPdp ? PersonBubble : ContactPcsIcon}
                iconClass={isSoldPdp && styles.soldIcon}
                dataTn="pdp-blurb-pcs"
            >
                <div className={styles.vipPdpDiv}>
                    <span className={styles.title}>{vipPdpBlurbTitle} </span>
                    {textDescription}
                    <div className={styles.textDiv}>{repInfo}</div>
                </div>
            </PdpSharedBlurb>
        </div>
    );
};

PdpSharedBlurbPcsComponent.propTypes = {
    user: PropTypes.object,
    item: PropTypes.object,
    itemId: PropTypes.string,
    vipUserRelationship: PropTypes.object,
    isSoldPdp: PropTypes.bool,
    isTopBanner: PropTypes.bool,
    relay: PropTypes.shape({
        environment: PropTypes.object,
    }),
};

export const PdpSharedBlurbPcs = createFragmentContainer(PdpSharedBlurbPcsComponent, {
    user: graphql`
        fragment PdpSharedBlurbPcs_user on User {
            serviceId
            isVerifiedTrade
            isVip
        }
    `,
    item: graphql`
        fragment PdpSharedBlurbPcs_item on Item {
            serviceId
        }
    `,
    vipUserRelationship: graphql`
        fragment PdpSharedBlurbPcs_vipUserRelationship on UserRelationship {
            userDataB {
                ... on VipRepData {
                    email
                    firstName
                    phoneNumber(formatType: INTERNATIONAL)
                }
            }
        }
    `,
});
